import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { clsx as c } from 'clsx'

import BillingConfigs from './components/BillingConfigs'
import CreateEnterprise from './components/CreateEnterprise'
import CreateSalesOwnedEnterprise from './components/CreateSalesOwnedEnterprise'
import DirectorySyncPage from './components/DirectorySyncSection'
import ManageEnterprises from './components/ManageEnterprises'

import styles from './styles.module.scss'

import Tabs from '@/components/Tabs'
import BillingOpsPage from '@/pages/app/BillingOps'
import PilotsPage from '@/pages/app/Pilots'
import { selectIsInternalAdmin } from '@/store/features/user/userSlice'

const EnterprisesPage = () => {
  const isInternalAdmin = useSelector(selectIsInternalAdmin)
  const navigate = useNavigate()
  const location = useLocation()
  const tabs = ['Create', 'Manage', 'Pilots', 'Billing', 'Other']
  const billingSubTabs = ['Custom pricing', 'Billing Ops']

  const isBilling = location.pathname.includes('/enterprises/billing')

  // Get the active tab based on the current path
  const getActiveTab = () => {
    if (location.pathname.endsWith('/manage')) return 1
    if (location.pathname.endsWith('/pilots')) return 2
    if (isBilling) return 3
    if (location.pathname.endsWith('/other')) return 4
    return 0 // Default to create
  }

  const handleTabChange = (index: number) => {
    switch (index) {
      case 0:
        navigate('/app/enterprises/create')
        break
      case 1:
        navigate('/app/enterprises/manage')
        break
      case 2:
        navigate('/app/enterprises/pilots')
        break
      case 3:
        navigate('/app/enterprises/billing/custom-pricing')
        break
      case 4:
        navigate('/app/enterprises/other')
        break
    }
  }

  const getActiveBillingSubTab = () => (location.pathname.endsWith('/ops') ? 1 : 0)

  const handleBillingSubTabChange = (index: number) => {
    navigate(index === 1 ? '/app/enterprises/billing/ops' : '/app/enterprises/billing/custom-pricing')
  }

  // Redirect to /create if we're at /enterprises
  useEffect(() => {
    if (location.pathname === '/app/enterprises') {
      navigate('/app/enterprises/create')
    }
  }, [location.pathname, navigate])

  // Authorization: only admin users can access this page
  if (!isInternalAdmin) {
    return (
      <div className={styles.container}>
        <p>You are not authorized to access this page</p>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Wispr Flow | Enterprises</title>
      </Helmet>
      <div className={c(styles.container, styles.enterprisesContainer)}>
        <div className={c(styles.tabTitle, styles.pageTabTitle)}>
          <h6>Enterprises</h6>
          <Tabs tabs={tabs} activeTab={getActiveTab()} setActiveTab={handleTabChange} />
        </div>
        {isBilling && (
          <div className={styles.subTabs}>
            <Tabs tabs={billingSubTabs} activeTab={getActiveBillingSubTab()} setActiveTab={handleBillingSubTabChange} />
          </div>
        )}
        <div className={styles.tabbedContent}>
          {location.pathname.endsWith('/create-sales-owned') ? (
            <CreateSalesOwnedEnterprise />
          ) : location.pathname.endsWith('/manage') ? (
            <ManageEnterprises />
          ) : location.pathname.endsWith('/pilots') ? (
            <PilotsPage />
          ) : isBilling ? (
            getActiveBillingSubTab() === 1 ? (
              <BillingOpsPage />
            ) : (
              <BillingConfigs />
            )
          ) : location.pathname.endsWith('/other') ? (
            <DirectorySyncPage />
          ) : (
            <CreateEnterprise />
          )}
        </div>
      </div>
    </>
  )
}

export default EnterprisesPage
